import React from "react";
import { ApolloError } from "@apollo/client";
import { getListQueryStatus, NodeID, QueryStatus } from "@hex-insights/app-modules";
import {
	ChildrenProps,
	compareObjects,
	compareStringsAscBase,
	DefaultContextValue,
	sort,
	useRequiredContext,
} from "@hex-insights/core";
import {
	AcademicTerm,
	OrderDirection,
	StudentFilterInput,
	StudentNamesAndImagesQuery,
	useModelFromID,
	useStudentNamePartsAndImagesQuery,
} from "../../../../../Utilities";

export type ReportCardSectionStudentsContextValue = {
	isLoading: boolean;
	data: StudentNamesAndImagesQuery | undefined;
	error: ApolloError | undefined;
	queryStatus: QueryStatus;
};

const ReportCardSectionStudentsContext = React.createContext<ReportCardSectionStudentsContextValue>(
	new DefaultContextValue("ReportCardSectionStudentsContext") as any,
);

export function useReportCardSectionStudentsContext() {
	return useRequiredContext(ReportCardSectionStudentsContext, "useReportCardSectionStudentsContext");
}

export type ReportCardSectionStudentsContextManagerProps = {
	academicTermID: AcademicTerm["id"];
	sectionID: NodeID;
} & Partial<ChildrenProps>;

export function ReportCardSectionStudentsContextManager({
	academicTermID,
	sectionID,
	children,
}: ReportCardSectionStudentsContextManagerProps) {
	const { filter, skip } = useStudentFilter(academicTermID, sectionID);

	const { loading, data, error } = useStudentNamePartsAndImagesQuery({
		variables: {
			filters: filter,
			order: { direction: OrderDirection.Asc },
		},
		skip,
	});

	const contextValue = React.useMemo(() => {
		const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "studentConnection" });

		let sortedData: typeof data;
		if (data) {
			sortedData = {
				...data,
				studentConnection: {
					...data.studentConnection,
					edges: sort(
						data.studentConnection.edges,
						compareObjects((e) => e.node.person.familyName, compareStringsAscBase()),
					),
				},
			};
		}

		return {
			isLoading: loading,
			data: sortedData,
			error,
			queryStatus,
		};
	}, [loading, data, error]);

	return (
		<ReportCardSectionStudentsContext.Provider value={contextValue}>
			{children}
		</ReportCardSectionStudentsContext.Provider>
	);
}

function useStudentFilter(academicTermID: AcademicTerm["id"], sectionID: NodeID) {
	const { modelName } = useModelFromID(sectionID);

	return React.useMemo<{ filter: StudentFilterInput; skip?: boolean }>(() => {
		switch (modelName) {
			case "CourseSection":
				return {
					filter: {
						studentCourseSectionEnrollments: [
							{
								courseSectionIDEQ: sectionID,
							},
						],
						reportCards: [
							{
								academicTermIDEQ: academicTermID,
								reportCardSections: [
									{
										reportCardSectionPolicy: [
											{
												courseSectionIDEQ: sectionID,
											},
										],
									},
								],
							},
						],
					},
				};
			case "HomeRoomSection":
				return {
					filter: {
						studentHomeRoomSectionEnrollments: [
							{
								homeRoomSectionIDEQ: sectionID,
							},
						],
						reportCards: [
							{
								academicTermIDEQ: academicTermID,
								reportCardSections: [
									{
										reportCardSectionPolicy: [
											{
												homeRoomSectionIDEQ: sectionID,
											},
										],
									},
								],
							},
						],
					},
				};
			default:
				return { filter: {}, skip: true };
		}
	}, [academicTermID, sectionID, modelName]);
}
